/* eslint-disable react/prop-types */
import _ from 'underscore';
import classnames from 'classnames';
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';

import { getAdPlacementComponent } from './get-ad-placement-component';
import { withUAPLThemedStyles } from '../with-themed-styles';
import { getAdPreviewWaterfallGridLayoutStyles } from '../styles';
import { pickSingleImageFromImageList } from '../ad-preview-model/use-ad-preview-model';

const WaterfallGridPlacement = ({
  renderPlacement, renderNextPlacement, hideRowGap, rowGap,
}) => {
  useEffect(() => {
    if (renderNextPlacement) {
      renderNextPlacement();
    }
  }, [renderNextPlacement]);

  return renderPlacement && renderPlacement() ? (
    <div style={{ marginBottom: hideRowGap ? 0 : rowGap }}>
      {renderPlacement()}
    </div>
  ) : null;
};

const WaterfallGridLayout /* : React.FC<WaterfallGridLayoutProps> */ = ({
  cardMinWidth = 540,
  columnGap = 24,
  rowGap = 24,
  placementList,
  onRenderPlacement,
  onRenderEmptyView,
  gridLayoutContainerClassName,
  classes,
}) => {
  const wrapperRef = useRef(null);
  const columnWidth = useRef(0);
  const leftRenderPlacementList = useRef([]);
  const [renderList, setRenderList] = useState([[]]);
  const [columnCount, setColumnCount] = useState(0);

  const emptyView = useMemo(() => {
    if (onRenderEmptyView) {
      return onRenderEmptyView(placementList);
    }
    return null;
  }, [onRenderEmptyView, placementList]);

  const renderPlacementComponentList = useMemo(() => {
    const ret = _.map(placementList, (placement, index) => {
      const { placementMeta, images, ...rest } = placement || {};
      const { placementType, hasMultiImages } = placementMeta || {};
      const AdPlacementComponent = getAdPlacementComponent(placementType);
      if (AdPlacementComponent) {
        // convert multi-image model to single-image model if needed
        let newPlacement = placement;
        if (!hasMultiImages && !_.isEmpty(images)) {
          const selectedImage = pickSingleImageFromImageList(images) || {};
          newPlacement = { placementMeta, ...selectedImage, ...rest };
        }
        return () => onRenderPlacement(newPlacement, AdPlacementComponent, index);
      }
      return () => null;
    });
    return ret;
  }, [onRenderPlacement, placementList]);

  const updateColumnCount = useCallback((entries) => {
    if (entries[0]) {
      const { clientWidth } = entries[0].target;

      if (clientWidth <= cardMinWidth) {
        setColumnCount(1);
        columnWidth.current = clientWidth;
      } else {
        const newColumnCount = Math.floor((clientWidth - cardMinWidth) / (cardMinWidth + columnGap)) + 1;
        // eslint-disable-next-line no-mixed-operators
        const newColumnWidth = Math.floor((clientWidth - (newColumnCount - 1) * columnGap) / newColumnCount);
        columnWidth.current = newColumnWidth;
        setColumnCount(newColumnCount);
      }
    }
  }, [cardMinWidth, columnGap]);

  const renderNextPlacement = (currentRenderList) => {
    if (!columnCount || !leftRenderPlacementList.current || _.isEmpty(leftRenderPlacementList.current)) {
      return;
    }
    let minHeight = Infinity;
    let minHeightIndex = 0;

    const newRenderList = currentRenderList.map(column => column.map(({ renderPlacement }) => ({ renderPlacement })));
    const renderPlacement = leftRenderPlacementList.current.shift();
    wrapperRef.current.querySelectorAll('.ad-preview-waterfall-grid-column').forEach((el, elIdx) => {
      if (el.clientHeight < minHeight) {
        minHeight = el.clientHeight;
        minHeightIndex = elIdx;
      }
    });

    newRenderList[minHeightIndex].push({
      renderPlacement,
      renderNextPlacement: () => renderNextPlacement(newRenderList),
    });
    setRenderList([...newRenderList]);
  };

  useEffect(() => {
    if (!columnCount || !renderPlacementComponentList || _.isEmpty(renderPlacementComponentList)) {
      return;
    }
    // rerender the waterfall grid
    leftRenderPlacementList.current = [...renderPlacementComponentList];
    const newRenderList = _.map(_.range(columnCount), () => []);
    const renderPlacement = leftRenderPlacementList.current.shift();
    newRenderList[0].push({
      renderPlacement,
      renderNextPlacement: () => renderNextPlacement(newRenderList),
    });
    setRenderList([...newRenderList]);
  }, [columnCount, renderPlacementComponentList]); // do not add other dependencies

  useEffect(() => {
    const resizeObserver = window.ResizeObserver && new ResizeObserver(_.debounce(updateColumnCount, 100));
    if (wrapperRef.current && resizeObserver) {
      resizeObserver.observe(wrapperRef.current);
    }
    return () => {
      if (wrapperRef.current && resizeObserver) {
        resizeObserver.unobserve(wrapperRef.current);
      }
    };
  }, [updateColumnCount]);

  if (_.isEmpty(renderPlacementComponentList)) {
    return emptyView;
  }

  return (
    <div className={classnames(classes.adPreviewWaterfallGridLayoutContainer, gridLayoutContainerClassName)} ref={wrapperRef} >
      {renderList.map((column, columnIndex) => (
        <div
          className="ad-preview-waterfall-grid-column"
          style={{
            marginRight: (columnIndex === renderList.length - 1) ? 0 : columnGap, flexGrow: 1, flexBasis: 0, width: columnWidth.current,
          }}
        >
          {column.map((columnItem, columnItemIdx) => <WaterfallGridPlacement rowGap={rowGap} hideRowGap={columnItemIdx === column.length - 1} renderPlacement={columnItem.renderPlacement} renderNextPlacement={columnItem.renderNextPlacement} />)}
        </div>
      ))}
    </div>
  );
};

export const AdPreviewWaterfallGridLayout = withUAPLThemedStyles(
  WaterfallGridLayout,
  getAdPreviewWaterfallGridLayoutStyles,
  { componentName: 'AdPreviewWaterfallGridLayout' }
);
